import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SecurityIcon from "@mui/icons-material/Security";
import { useMutation } from "@apollo/client";
import { VERIFY_USER } from "../data/graphql/mutations";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    maxWidth: 600,
    width: "100%",
    margin: 16,
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  padding: theme.spacing(2, 0, 1, 0),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: "white",
  margin: theme.spacing(-3, -3, 0, -3),
  marginBottom: theme.spacing(2),
}));

const CheckboxContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.primary.light,
  },
}));

interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  userAuth0Id: string;
  onVerificationComplete: () => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  open,
  onClose,
  userAuth0Id,
  onVerificationComplete,
}) => {
  const [ageVerified, setAgeVerified] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [privacyPolicyAgreed, setPrivacyPolicyAgreed] = useState(false);
  const [error, setError] = useState<string>("");

  const [verifyUser, { loading }] = useMutation(VERIFY_USER, {
    onCompleted: () => {
      onVerificationComplete();
      onClose();
    },
    onError: (error) => {
      setError(error.message || "Failed to verify account. Please try again.");
      console.error("Verification error:", error);
    },
  });

  const handleSubmit = async () => {
    if (!ageVerified || !locationVerified || !privacyPolicyAgreed) {
      setError("Please complete all verification requirements to continue.");
      return;
    }

    setError("");

    try {
      await verifyUser({
        variables: {
          user_auth0_id: userAuth0Id,
          age_verification: ageVerified,
          location_verification: locationVerified,
          privacy_policy_agreement: privacyPolicyAgreed,
        },
      });
    } catch (err) {
      console.error("Verification submission error:", err);
    }
  };

  const allRequirementsMet =
    ageVerified && locationVerified && privacyPolicyAgreed;

  return (
    <StyledDialog
      open={open}
      onClose={undefined} // Prevent closing by clicking outside or ESC
      disableEscapeKeyDown
      maxWidth="md"
    >
      <HeaderBox>
        <VerifiedUserIcon sx={{ fontSize: 48, mb: 1, mt: 2 }} />
        <DialogTitle
          sx={{
            color: "white",
            textAlign: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            padding: 0,
          }}
        >
          Account Verification Required
        </DialogTitle>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255, 255, 255, 0.9)", textAlign: "center", mt: 1 }}
        >
          To continue using InkBowl, please verify your account
        </Typography>
      </HeaderBox>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <SecurityIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">
              Verification Requirements
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please confirm the following information to ensure compliance with
            our terms of service and provide you with the best possible
            experience.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <CheckboxContainer>
          <FormControlLabel
            control={
              <Checkbox
                checked={ageVerified}
                onChange={(e) => setAgeVerified(e.target.checked)}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  I am 16 years old or older
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  InkBowl requires users to be at least 16 years of age to
                  create and maintain an account.
                </Typography>
              </Box>
            }
          />
        </CheckboxContainer>

        <CheckboxContainer>
          <FormControlLabel
            control={
              <Checkbox
                checked={locationVerified}
                onChange={(e) => setLocationVerified(e.target.checked)}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  I reside in the United States, United Kingdom, European Union,
                  or Canada
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Currently, InkBowl services are available to residents of
                  these regions.
                </Typography>
              </Box>
            }
          />
        </CheckboxContainer>

        <CheckboxContainer>
          <FormControlLabel
            control={
              <Checkbox
                checked={privacyPolicyAgreed}
                onChange={(e) => setPrivacyPolicyAgreed(e.target.checked)}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  I have read and agree to InkBowl&apos;s{" "}
                  <Link
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: "bold" }}
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: "bold" }}
                  >
                    Terms of Service
                  </Link>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Please review our policies to understand how we handle your
                  data and account.
                </Typography>
              </Box>
            }
          />
        </CheckboxContainer>

        <Divider sx={{ my: 2 }} />

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Why do we need this information?</strong>
            <br />
            These requirements help us maintain a safe, legal, and compliant
            platform for all users. Your privacy and security are our top
            priorities.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={!allRequirementsMet || loading}
          sx={{
            width: "100%",
            py: 1.5,
            fontSize: "1rem",
            fontWeight: "bold",
            borderRadius: 2,
            textTransform: "none",
            background: allRequirementsMet
              ? "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)"
              : undefined,
            "&:hover": {
              background: allRequirementsMet
                ? "linear-gradient(45deg, #1976D2 30%, #0288D1 90%)"
                : undefined,
            },
          }}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <VerifiedUserIcon />
            )
          }
        >
          {loading ? "Verifying Account..." : "Complete Verification"}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default VerificationModal;
